import React from 'react';

import cs from 'classnames';
import styled from 'styled-components';

import {RichTextarea} from 'app/components/general/RichTextarea';
import {ALL_BUTTONS} from 'app/components/general/RichTextarea/PreparedFroalaEditor';
import {useCurrentUserState} from 'app/current-user/api';
import {FormControlGroup, ShowNonFieldFormErrors} from 'app/general/forms';
import {ImageAttachmentsUploader} from 'app/general/uploader/components/ImageAttachmentsUploader';
import {ImagePreview} from 'app/general/uploader/components/ImagePreview';
import {useAttachmentsUploader} from 'app/general/uploader/hooks';
import {useAccountSettingsState} from 'app/settings/api';
import {convertLocationParamsToFetchParams, useTeams} from 'app/teams/api';
import {TeamsSelectMulti} from 'app/teams/components/TeamsSelect';
import {getMediaPath, removeMediaPath} from 'app/utils/fineuploader';
import {gt} from 'app/utils/i18n';

import type {ActionMeta} from 'app/components/general/SweetReactSelect';
import type {
  AttachedImage,
  BaseAttachedImage,
  BasicTeamSerializedType,
} from 'sweet-types';

interface NameProps {
  autoFocus: boolean;
  onChange: (name: string) => void;
  name: string;
}

export function FolderName({autoFocus, name, onChange}: NameProps) {
  return (
    <FormControlGroup field="name" className="flex-grow-1 mb-0">
      <input
        name="name"
        className="form-control mb-2 mt-2"
        value={name}
        onChange={(e) => onChange(e.target.value)}
        placeholder={gt('Folder Name')}
        autoFocus={autoFocus}
      />
      <ShowNonFieldFormErrors />
    </FormControlGroup>
  );
}

interface DescriptionProps {
  objectId: number | string;
  onCancel: () => void;
  onChange: (content: string) => void;
  content: string;
}

export function FolderDescription({
  objectId,
  onCancel,
  content,
  onChange,
}: DescriptionProps) {
  return (
    <div className="mb-2">
      <RichTextarea
        value={content}
        onChange={onChange}
        contentType="folder"
        objectId={objectId}
        options={{
          toolbarSticky: false,
          heightMax: 140,
          placeholderText: gt('Sweeten up the folder with some description!'),
          toolbarButtons: {
            moreText: {
              buttons: [...ALL_BUTTONS.format, ...ALL_BUTTONS.paragraph],
              buttonsVisible: 5,
            },
            moreRich: {
              buttons: [],
              buttonsVisible: 0,
            },
            moreMisc: {
              buttons: ALL_BUTTONS.meta,
              align: 'right',
              buttonsVisible: 2,
            },
          },
        }}
        onCloseRequest={onCancel}
      />
    </div>
  );
}

export interface FolderThumbnailProps {
  thumbnails: Array<AttachedImage>;
  onChange: (thumbnails: Array<AttachedImage>) => void;
}

export function FolderThumbnail({thumbnails, onChange}: FolderThumbnailProps) {
  const settings = useAccountSettingsState().data!;
  const {uploaderRef, setSubmittedImages, removeAttachment} =
    useAttachmentsUploader();

  function addThumbnail(thumbnail: BaseAttachedImage) {
    const thumbnails: Array<AttachedImage> = [
      {image_ref: thumbnail.image_ref, embed_thumb: '', embed_html: ''},
    ];
    onChange(thumbnails);
  }

  function removeThumbnail(image_ref: string) {
    onChange([]);
    const media_path = getMediaPath(settings.MEDIA_URL);
    const key = removeMediaPath(image_ref, settings.MEDIA_URL);
    removeAttachment(media_path + key);
  }

  return (
    <div className={cs('d-flex', {'mt-2': thumbnails.length > 0})}>
      {thumbnails.length > 0 ? (
        <StyledImageContainerDiv>
          <ImagePreview
            attachment={{image_ref: thumbnails[0].image_ref ?? ''}}
            onRemoveAttachment={removeThumbnail}
            imageSize="original"
            spinnerSizeClass="spinner-border-sm"
          />
        </StyledImageContainerDiv>
      ) : (
        <ImageAttachmentsUploader
          uploaderRef={uploaderRef}
          onAddImage={addThumbnail}
          setSubmittedImages={setSubmittedImages}
          inputLabel=""
          iconSizeClass="fa-3x"
        />
      )}
    </div>
  );
}

interface FolderTeamProps {
  allowedTeams: Array<BasicTeamSerializedType>;
  isSelectOnly: boolean;
  onTeamChange: (
    teams: Array<BasicTeamSerializedType>,
    action: ActionMeta<BasicTeamSerializedType>
  ) => Promise<any>;
  teams: Array<BasicTeamSerializedType>;
}

export function FolderTeamSelector({
  teams,
  allowedTeams,
  isSelectOnly,
  onTeamChange,
}: FolderTeamProps) {
  const {is_super_manager} = useCurrentUserState().data!;

  const {data} = useTeams(
    convertLocationParamsToFetchParams({
      id: teams.map((team) => team.id).join(','),
    })
  );

  if (!data) {
    return null;
  }

  let placeholder = gt('Select a team to assign to.');
  if (isSelectOnly && allowedTeams.length === 0) {
    placeholder = gt('Assign a team to folder one level up first.');
  } else if (!isSelectOnly && is_super_manager) {
    placeholder = gt('Select or create a team to assign to');
  }

  function onChangeTeam(
    teams: Array<BasicTeamSerializedType>,
    action: ActionMeta<BasicTeamSerializedType>
  ) {
    return onTeamChange(teams, action);
  }

  const params = {};
  if (isSelectOnly) {
    params['id'] = allowedTeams.map((team) => team.id).join(',');
  }

  return (
    <TeamsSelectMulti
      selected={data.results}
      onChange={onChangeTeam}
      refetchDataOnOpen={false}
      placeholder={placeholder}
      canCreate={is_super_manager}
      selectProps={{
        noOptionsMessage: () => {
          if (isSelectOnly && allowedTeams.length > 0) {
            return gt('Only teams from the folder one level up can be added.');
          } else if (isSelectOnly) {
            return gt(
              'Please add teams to the folder one level up before assigning them here.'
            );
          } else {
            return gt('No available teams.');
          }
        },
      }}
      params={params}
    />
  );
}

const StyledImageContainerDiv = styled.div`
  img {
    max-width: 100px;
    max-height: 100px;
    object-fit: contain;
  }
`;
